import React from "react";
import { Gift, MapPin, Calendar, Clock } from "lucide-react";

function formatDate(d) {
  if (!d) return "";
  try {
    return new Date(d + "T00:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
  } catch {
    return d;
  }
}

function fmtTimeRange(period) {
  if (!period) return "";
  const start = (period.pickup_start_time || period.distribution_time || "").trim();
  if (!start) return period.distribution_time || "";
  const end = (period.pickup_end_time || "").trim();
  if (!end) return period.distribution_time || "";
  try {
    const fmt = (hhmm) => {
      const [h, m] = hhmm.split(":").map(Number);
      const suffix = h < 12 ? "AM" : "PM";
      const dh = (h % 12) || 12;
      return `${dh}:${String(m).padStart(2, "0")} ${suffix}`;
    };
    return `${fmt(start)} \u2013 ${fmt(end)}`;
  } catch {
    return period.distribution_time || "";
  }
}

/**
 * Compact call-out box used inside Sharing Tree signup contexts to show
 * where/when distribution happens for the configured year.
 * Renders nothing if no distribution info is present.
 */
export default function DistributionCallout({ period, year }) {
  if (!period) return null;
  const timeDisplay = fmtTimeRange(period);
  const hasInfo = period.location_name || period.location_address ||
                  period.distribution_date || timeDisplay || period.custom_message;
  if (!hasInfo) return null;

  return (
    <div
      data-testid="distribution-callout"
      className="rounded-xl p-4 border-l-4"
      style={{
        background: "#FBF6EA",
        borderLeftColor: "var(--fb-accent)",
      }}
    >
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
             style={{ background: "var(--fb-accent)", color: "white" }}>
          <Gift className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-wider font-semibold mb-1" style={{ color: "var(--fb-warning)" }}>
            Sharing Tree Distribution {year ? `· ${year}` : ""}
          </div>
          <dl className="space-y-1.5 text-sm">
            {period.location_name && (
              <div className="flex items-start gap-1.5">
                <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: "var(--fb-text-muted)" }} />
                <span>
                  <span className="font-semibold">{period.location_name}</span>
                  {period.location_address && <span style={{ color: "var(--fb-text-muted)" }}> — {period.location_address}</span>}
                </span>
              </div>
            )}
            {period.distribution_date && (
              <div className="flex items-start gap-1.5">
                <Calendar className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: "var(--fb-text-muted)" }} />
                <span>{formatDate(period.distribution_date)}</span>
              </div>
            )}
            {timeDisplay && (
              <div className="flex items-start gap-1.5">
                <Clock className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: "var(--fb-text-muted)" }} />
                <span>{timeDisplay}</span>
              </div>
            )}
          </dl>
          {period.custom_message && (
            <p className="text-sm mt-2 italic" style={{ color: "var(--fb-text-muted)" }}>
              {period.custom_message}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
